import { createContext, useCallback, useRef, useState } from 'react';
import {
  CreateActionsType,
  UploadStepsEnum,
  ModalContextType,
  API_CONSTANTS,
  MediaTypeEnum,
  AudioModelType,
} from '../utils';
import { getUploadId, putPreSignedUrl, uploadFileToPresignedUrl } from '../services';
import { useAppContext, useHandleApiCall, useNotification } from '../hooks';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { FeatureType, useGetUserEligibility } from '../hooks/useGetUserEligibility';

export const CreateMediaModalContext = createContext<ModalContextType>(null);

export const CreateMediaModalProvider = ({ children }) => {
  const { showNotification } = useNotification();
  const { post } = useHandleApiCall();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMediaDetails, setSelectedMediaDetails] = useState<CreateActionsType>(null);
  const [currentModalStep, setCurrentModalStep] = useState<UploadStepsEnum>(
    UploadStepsEnum.SelectFile
  );
  const [uploadURL, setUploadURL] = useState('');
  const [fileDetails, setFileDetails] = useState<Record<string, string>>(null);
  const [mediaName, setMediaName] = useState('');
  const [uploadId, setUploadId] = useState('');
  const [fileUploadPercent, setFileUploadPercent] = useState(0);
  const [extractAudioFromAvatar, setExtractAudioFromAvatar] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const [isRetryDisable, setIsRetryDisable] = useState(false);
  const [audioModel, setAudioModel] = useState<AudioModelType>(AudioModelType.FISHAUDIO)

  const abortController = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const {
    websocket: { sendMessage },
  } = useAppContext();

  const handleFileSelect = (files) => {
    const filePreview = URL.createObjectURL(files);
    files.size && setFileDetails(files);
    files.size && setUploadURL(filePreview);
  };

  const handleMediaNameChange = (value: string) => {
    setMediaName(value);
  };

  const handleCloseModal = () => {
    if (abortController.current) {
      abortController.current.abort();
    }

    setIsOpen(false);
    setSelectedMediaDetails(null);
    setUploadURL('');
    setCurrentModalStep(UploadStepsEnum.SelectFile);
    setMediaName('');
    setFileDetails(null);
    setFileUploadPercent(0);
    setIsUploadError(false);
    setUploadId('');
    setIsRetryDisable(false);
  };

  const { push } = useRouter();
  const handlePreview = () => {
    if (selectedMediaDetails?.type === MediaTypeEnum.AUDIO) {
      push(`cloned-audios/${uploadId}`);
    } else {
      push(`created-avatar/${uploadId}`);
    }
  };

  const { mutateAsync: getUserEligibility, isLoading } = useGetUserEligibility();

  const handleClick = useCallback(async (type: CreateActionsType) => {
    if (type?.type === MediaTypeEnum.AUDIO || type?.type === MediaTypeEnum.AVATAR) {
      const eligibility = await getUserEligibility({
        featureType:
          type?.type === MediaTypeEnum.AUDIO ? FeatureType.AUDIO_CLONE : FeatureType.AVATAR_CLONE,
      });
      if (!eligibility.eligible && type?.type === MediaTypeEnum.AVATAR) {
        enqueueSnackbar(
          `You have reached maximum avatar creation limit of your plan. Kindly upgrade it.`,
          {
            variant: 'error',
          }
        );
        return;
      }
    }
    setIsOpen((prevValue) => !prevValue);
    setSelectedMediaDetails({ ...type });
  }, []);

  const handleFileUploadPercentage = useCallback((value: number) => {
    setFileUploadPercent(value);
  }, []);

  const handleFileUpload = async (files) => {
    try {
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const { uploadId, preSignedUrls, format } = await putPreSignedUrl(files);
      await uploadFileToPresignedUrl(uploadId, preSignedUrls, files, handleFileUploadPercentage, signal);
      let mimeType = files.type;
      if (files?.name?.toLowerCase()?.endsWith('.mov')) {
        mimeType = 'video/mp4';
      }
      const uploadDetails = await getUploadId(preSignedUrls[0].url, format, mimeType, signal);
      return uploadDetails;
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        enqueueSnackbar('Upload cancelled.', { variant: 'info' });
      } else {
        setIsUploadError(true);
      }
    }
  };

  const audioCloningMutation = post(API_CONSTANTS.AUDIO_CLONE.key, API_CONSTANTS.AUDIO_CLONE.path);
  const avatarCreationMutation = post(
    API_CONSTANTS.CREATE_AVATAR.key,
    API_CONSTANTS.CREATE_AVATAR.path
  );

  const handleOnSubmit = async (tags: any = []) => {
    try {
      setCurrentModalStep(UploadStepsEnum.FileUploading);
      const uploadDetails = await handleFileUpload(fileDetails);
      if (uploadDetails) {
        setUploadId(uploadDetails.id);
        setCurrentModalStep(UploadStepsEnum.FileUploaded);
        if (selectedMediaDetails?.type === MediaTypeEnum.AUDIO) {
          await audioCloningMutation.mutateAsync({ name: mediaName, uploadId: uploadDetails.id, tags: tags, model: audioModel });
        } else {
          await avatarCreationMutation.mutateAsync({
            name: mediaName,
            uploadId: uploadDetails.id,
            audioExtract: extractAudioFromAvatar,
            tags: tags,
           ...(extractAudioFromAvatar && { audioModel }),
          });
        }
        sendMessage({ event: 'register', data: { id: uploadDetails.id } });
        setCurrentModalStep(UploadStepsEnum.InProgress);
      }
    } catch (error) {
      console.error('Error while uploading file:', error);

      if (error?.response?.data?.message?.includes('User cannot have more than'))
        enqueueSnackbar('A maximum of 10 audio clones is permitted.', { variant: 'error' });
      else
        enqueueSnackbar('We hit a glitch in the matrix! Please try again.', { variant: 'error' });

      handleCloseModal();
    }
  };

  const handleRetry = async () => {
    setIsRetryDisable(true);
    setUploadId('');
    setUploadURL('');
    setFileUploadPercent(0);
    setIsUploadError(false);
    handleOnSubmit();
  };

  const contextValue: ModalContextType = {
    isOpen,
    selectedMediaDetails,
    handleClick,
    handleCloseModal,
    currentModalStep,
    handleFileSelect,
    fileDetails,
    uploadURL,
    mediaName,
    handleMediaNameChange,
    audioModel,
    setAudioModel,
    handleOnSubmit,
    handleRetry,
    fileUploadPercent,
    extractAudioFromAvatar,
    setExtractAudioFromAvatar,
    uploadId,
    isUploadError,
    isRetryDisable,
    handlePreview,
  };

  return (
    <CreateMediaModalContext.Provider value={contextValue}>
      {children}
    </CreateMediaModalContext.Provider>
  );
};
